import _ from "lodash";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { TextField, CircularProgress, Autocomplete } from "@mui/material";
import { useHomologarApi, useApi, useCurrentHealthCenterAlias } from "../../../../utils/hooks";
import SearchOption from "./SearchOption";
import {
  DEFAULT_SEARCH_OPTIONS,
  resultsMapper,
  ItemType,
  OrderType,
} from "./utils/constants";
import { templateTypeMapper, procedureTypeFromTemplateType } from "../templates/utils";
import { matchSorter } from "match-sorter";
import { getLabel } from "./utils";
import ConfirmationDialog from "../../../../utils/ButtonWithConfirmation/ConfirmationDialog";
import HomologarTemplateUpdater from "../templates/Update";
import ManualTemplateUpdater from "../../medicalDocument/templates/updater";
import { getEventNomenclatureEventHandler } from "./utils/events";

const ConditionalTemplateUpdater = ({ orderType, ...props }) => {
  const resolvedOrderType =
    orderType === OrderType.PRACTICES
      ? props.template?.procedureType ?? orderType
      : orderType;
  const type = templateTypeMapper[resolvedOrderType] ?? templateTypeMapper[OrderType.IMAGING];
  if (orderType === OrderType.MANUAL) {
    return <ManualTemplateUpdater type={type} {...props} />;
  }
  return (
    <HomologarTemplateUpdater orderType={resolvedOrderType} type={type} {...props} />
  );
};

// Las plantillas homologar (order-homologar) llegan hidratadas con `items`; si
// quedaron sin ítems (p.ej. todas sus prácticas archivadas) no se muestran. Las
// manuales no traen `items` (usan `content`), así que no se filtran.
const isEmptyHomologarTemplate = (template) =>
  Array.isArray(template.items) && template.items.length === 0;

const getTemplateOptions = (templates) => {
  return templates
    .filter((item) => !isEmptyHomologarTemplate(item))
    .map((item) => ({
      ...resultsMapper(item, ItemType.TEMPLATE, procedureTypeFromTemplateType(item.type)),
      template: item,
    }));
};

const NomenclatureSearch = ({
  onSelect,
  procedureType,
  searchOptions,
  templates,
  setTemplates,
  minQueryLength,
  setSelectedTemplate,
  disabled,
  searchPlaceholder,
  showTypeChips,
  appVersion,
  isNomenclatureV3,
  belowSearch,
  ...props
}) => {
  const { searchNomenclature, searchTemplates } =
    searchOptions ?? DEFAULT_SEARCH_OPTIONS;
  const homologarApi = useHomologarApi();
  const recetarioApi = useApi();
  const currentHealthCenterAlias = useCurrentHealthCenterAlias();

  const [inputValue, setInputValue] = useState("");
  const [previousInputValue, setPreviousInputValue] = useState("");
  const [previousOptions, setPreviousOptions] = useState([]);

  const templateDefaultOptions = useMemo(
    () => getTemplateOptions(templates),
    [templates]
  );

  const isManual = procedureType === OrderType.MANUAL;

  useEffect(() => {
    setInputValue("");
    setPreviousInputValue("");
    setPreviousOptions([]);
    setOpen(false);
    setOptions(isManual ? templateDefaultOptions : []);
  }, [templateDefaultOptions, isManual]);

  /**
   * options -> {
   *   name: string,
   *   itemType: ItemType,
   *   metadata: {
   *     template?: Template
   *   }
   * }[]
   */
  const [options, setOptions] = useState(
    isManual ? templateDefaultOptions : []
  );

  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [templateToUpdate, setTemplateToUpdate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [open, setOpen] = useState(false);

  const removeTemplate = (template) => {
    setTemplates((prev) => prev.filter((t) => t.id !== template.id));
  };
  const deleteTemplate = () =>
    recetarioApi
      .deleteTemplate(templateToDelete.id)
      .tap(() => removeTemplate(templateToDelete))
      .tap(() => setTemplateToDelete(null));

  const nomenclatureSearch = useCallback(
    async (query) => {
      if (!searchNomenclature) {
        return [];
      }

      const isPractices = procedureType === OrderType.PRACTICES;
      let response = isPractices
        ? await homologarApi.searchPractices(query, 40, isNomenclatureV3)
        : await homologarApi.searchNomenclature(query, procedureType, 40, isNomenclatureV3);

      // El buscador v3 siempre trae el catálogo unificado (imaging + laboratory). Cuando
      // el tipo está bloqueado (locked a imaging/laboratory tras elegir el 1er ítem), se
      // filtra client-side para no mezclar tipos: una plantilla/card es de un solo tipo.
      // Los ítems v3 traen procedureType ("imaging"/"laboratory"), así que el filtro es fiable.
      if (isNomenclatureV3 && !isPractices) {
        response = response.filter((item) => item.procedureType === procedureType);
      }

      if (response.length === 0) {
        getEventNomenclatureEventHandler(procedureType, appVersion).onNoResults(query);
      }

      // PRACTICES is UI-only (never a real procedureType / never sent to the API).
      // Los resultados v3 traen procedureType; en PRACTICES (sin lock) se usa tal cual,
      // sin estampar "practices" sobre cada ítem.
      return response.map((item) =>
        resultsMapper(
          item,
          ItemType.NOMENCLATURE,
          item.procedureType ?? (isPractices ? null : procedureType)
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchNomenclature, procedureType, appVersion, isNomenclatureV3]
  );

  const templatesSearch = useCallback(
    (query) => {
      if (!searchTemplates) {
        return templateDefaultOptions;
      }

      const response = matchSorter(templates, query, {
        keys: ["name"],
        threshold: matchSorter.rankings.FUZZY,
        keepDiacritics: true,
        baseSort: (a, b) => (a.index < b.index ? -1 : 1),
      });

      return getTemplateOptions(response);
    },
    [searchTemplates, templates, templateDefaultOptions]
  );

  const search = useMemo(
    () =>
      _.debounce(async (query) => {
        setLoading(true);
        try {
          const normalizedQuery = (query ?? "").trim();
          if (normalizedQuery.length === 0) {
            setOptions(isManual ? templateDefaultOptions : []);
            return;
          }

          if (normalizedQuery.length < minQueryLength) {
            setOptions(templatesSearch(normalizedQuery));
            return;
          }

          const templateResponse = templatesSearch(normalizedQuery);
          const nomenclatureResponse = await nomenclatureSearch(
            normalizedQuery
          );

          const response = [...templateResponse, ...nomenclatureResponse];
          setOptions(response);

          return response;
        } catch (err) {
          console.error("Error fetching nomenclatures or templates:", err);
        } finally {
          setLoading(false);
        }
      }, 300),
    [
      minQueryLength,
      templatesSearch,
      nomenclatureSearch,
      isManual,
      templateDefaultOptions,
    ]
  );

  useEffect(() => {
    return () => {
      search.cancel();
    };
  }, [search]);

  useEffect(() => {
    search(inputValue);
  }, [inputValue, search, procedureType]);

  useEffect(() => {
    if (inputValue.length >= previousInputValue.length) {
      setPreviousOptions(options);
    }
  }, [inputValue, previousInputValue, options]);

  return (
    <>
      <Autocomplete
        fullWidth
        loading={loading}
        options={options}
        noOptionsText={null}
        getOptionLabel={(option) => option?.name ?? ""}
        value={selectedValue}
        disabled={disabled}
        open={open && options.length > 0}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        {...props}
        renderOption={(props, option) => (
          <li {...props}>
            <SearchOption
              option={option}
              setTemplateToDelete={setTemplateToDelete}
              setTemplateToUpdate={setTemplateToUpdate}
              isPractices={showTypeChips ?? procedureType === OrderType.PRACTICES}
              currentHealthCenterAlias={currentHealthCenterAlias}
              {...props}
            />
          </li>
        )}
        onChange={(_event, value) => {
          onSelect(value);
          setSelectedValue(null);
          setInputValue("");
          setPreviousInputValue("");
          setPreviousOptions([]);

          getEventNomenclatureEventHandler(procedureType, appVersion).onSelectedOption(
            value,
            inputValue,
            options
          );
        }}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          const newValue = newInputValue ?? "";

          if (
            previousInputValue.length > 0 &&
            newValue.length === 0 &&
            previousOptions.length > 0
          ) {
            getEventNomenclatureEventHandler(procedureType, appVersion).onFullInputDelete(
              previousInputValue,
              previousOptions
            );
          }
          if (newValue.length === 0) {
            setPreviousOptions([]);
            setPreviousInputValue("");
          }

          if (newValue.length >= previousInputValue.length) {
            setPreviousInputValue(inputValue);
          }
          setInputValue(newValue);
        }}
        filterOptions={_.identity}
        isOptionEqualToValue={(option, value) =>
          option.name === value.name && option.itemType === value.itemType
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={searchPlaceholder ?? getLabel(searchOptions, procedureType)}
            variant="outlined"
            value={inputValue}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </>
              ),
            }}
          />
        )}
      />
      {belowSearch}
      {!_.isEmpty(templateToUpdate) && (
        <ConditionalTemplateUpdater
          originalTemplate={templateToUpdate}
          setTemplates={setTemplates}
          template={templateToUpdate}
          open={!_.isEmpty(templateToUpdate)}
          toggleOpen={() => setTemplateToUpdate(null)}
          setSelectedTemplate={setSelectedTemplate}
          orderType={procedureType}
          templates={templates}
          isNomenclatureV3={isNomenclatureV3}
        />
      )}
      <ConfirmationDialog
        isVisible={!_.isEmpty(templateToDelete)}
        setIsVisible={() => setTemplateToDelete(null)}
        message={
          !_.isEmpty(templateToDelete)
            ? `¿Está seguro de que desea eliminar la plantilla ${templateToDelete.name}?`
            : ""
        }
        onConfirm={deleteTemplate}
      />
    </>
  );
};

export default NomenclatureSearch;
